import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStripe } from '@stripe/react-stripe-js';
import { useStripeOnMount } from '@/provider/stripeLoader';
import { useLocation } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { selectPaymentMethods, selectSubscription } from '@/features/billing/billingSlice';
import {
  fetchPaymentMethods,
  fetchSubscription,
  fetchUsage,
  setPaymentMethodDefault,
} from '@/features/billing/billingThunks';
import { fetchWorkspaceById } from '@/features/workspace/workspaceThunks';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import Loader from '@/components/ui/Loader';
import { Separator } from '@/components/ui/separator';
import Title from '../ui/Title';
import UpgradePaymentMethodSelector from './UpgradePaymentMethodSelector';
import PastDuePaymentForm from './PastDuePaymentForm';
import { formatPrice, getPlanDisplayName, isPaymentOverdueStatus } from './usageExpiredUtils';
import { isUnpaidModalRequested } from '@/lib/billingRegularization';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'react-toastify';

export default function UnpaidModal() {
  const { t } = useTranslation('billing');
  const dispatch = useAppDispatch();
  const stripe = useStripe();
  const subscription = useAppSelector(selectSubscription);
  const paymentMethods = useAppSelector(selectPaymentMethods);
  const workspaceId = useAppSelector((s: RootState) => s.workspace.currentWorkspace?.id);
  const workspace = useAppSelector((s: RootState) => s.workspace.currentWorkspace);
  const user = useAppSelector((s: RootState) => s.auth.user);

  const [open, setOpen] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const [useNewCard, setUseNewCard] = useState(false);
  const [isPaying, setIsPaying] = useState(false);

  const location = useLocation();
  const [autoOpenedLocationKey, setAutoOpenedLocationKey] = useState<string | null>(null);

  const isPastDue = isPaymentOverdueStatus(subscription?.status);
  useStripeOnMount(isPastDue && workspace?.productAccessStatus !== 'SUSPENDED');

  // Arrivée par le lien de relance : la modale s'ouvre d'elle-même, une fois par navigation.
  if (
    isPastDue &&
    isUnpaidModalRequested(location.state) &&
    autoOpenedLocationKey !== location.key
  ) {
    setAutoOpenedLocationKey(location.key);
    setOpen(true);
  }
  const paymentIntentClientSecret = subscription?.paymentIntentClientSecret ?? null;
  // On règle une facture déjà émise : c'est son total qui sera prélevé, pas le tarif du plan.
  const amountDue = subscription?.latestInvoiceTotal ?? subscription?.amount ?? null;
  const canPay = Boolean(subscription?.paymentRequired && paymentIntentClientSecret);

  useEffect(() => {
    if (!workspaceId || !isPastDue) return;
    void dispatch(fetchSubscription(workspaceId));
    void dispatch(fetchPaymentMethods(workspaceId));
    void dispatch(fetchWorkspaceById(workspaceId));
  }, [workspaceId, isPastDue, dispatch]);

  useEffect(() => {
    if (paymentMethods?.length) {
      const defaultCard = paymentMethods.find((pm) => pm.isDefault);
      setSelectedPaymentMethodId(defaultCard?.id ?? paymentMethods[0]?.id ?? null);
      setUseNewCard(false);
    } else {
      setSelectedPaymentMethodId(null);
      setUseNewCard(true);
    }
  }, [paymentMethods]);

  if (!isPastDue || workspace?.productAccessStatus === 'SUSPENDED') return null;

  const intervalLabel =
    subscription?.interval === 'year' ? t('unpaid.intervalYear') : t('unpaid.intervalMonth');

  const handlePayWithSavedCard = async () => {
    if (!workspaceId || !paymentIntentClientSecret || !stripe) {
      toast.error(t('unpaid.toast.notReady'));
      return;
    }

    const pmToUse = selectedPaymentMethodId ?? paymentMethods?.find((pm) => pm.isDefault)?.id;
    if (!pmToUse) {
      toast.error(t('unpaid.toast.noCard'));
      return;
    }

    setIsPaying(true);
    try {
      await dispatch(setPaymentMethodDefault({ workspaceId, paymentMethodId: pmToUse })).unwrap();

      const { error: paymentError } = await stripe.confirmCardPayment(paymentIntentClientSecret, {
        payment_method: pmToUse,
      });

      if (paymentError) {
        toast.error(paymentError.message ?? t('unpaid.toast.failed'));
        return;
      }

      await dispatch(fetchSubscription(workspaceId)).unwrap();
      await Promise.all([
        dispatch(fetchUsage(workspaceId)),
        dispatch(fetchPaymentMethods(workspaceId)),
        dispatch(fetchWorkspaceById(workspaceId)),
      ]);
      toast.success(t('unpaid.toast.success'));
    } catch (err: unknown) {
      const message = (err as { message?: string })?.message;
      toast.error(message ?? t('unpaid.toast.error'));
    } finally {
      setIsPaying(false);
    }
  };

  const regularizationBanner = workspace?.billingRegularizationBanner;
  const hasGraceBanner = Boolean(regularizationBanner);
  const dialogOpen = hasGraceBanner ? open : true;

  const handleOpenChange = (next: boolean) => {
    if (hasGraceBanner) {
      setOpen(next);
    }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={handleOpenChange}>
      {hasGraceBanner && regularizationBanner && (
        <div className="fixed top-4 left-1/2 z-40 -translate-x-1/2">
          <DialogTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 max-w-[400px] rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors cursor-pointer"
            >
              <AlertTriangle className="size-4 shrink-0" />
              <span className="text-left">{regularizationBanner.message}</span>
            </button>
          </DialogTrigger>
        </div>
      )}
      <DialogContent
        className="max-w-[1350px] max-h-[90vh] overflow-y-auto"
        hideCloseButton={!hasGraceBanner}
        onInteractOutside={!hasGraceBanner ? (e) => e.preventDefault() : undefined}
        onEscapeKeyDown={!hasGraceBanner ? (e) => e.preventDefault() : undefined}
      >
        <DialogHeader>
          <DialogTitle>{t('unpaid.title')}</DialogTitle>
          <DialogDescription>{t('unpaid.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-8">
          <div className="p-4 items-center rounded-[20px] bg-[#1F1F1F]/3 flex gap-4">
            <Avatar className="size-12">
              <AvatarImage src={user?.pictureUrl} />
              <AvatarFallback>
                {user?.firstName?.charAt(0) ?? ''}
                {user?.lastName?.charAt(0) ?? ''}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-xl font-medium">{workspace?.name}</p>
              {subscription?.planName && (
                <>
                  <p className="text-secondary text-base">
                    {t('common.plan')} :{' '}
                    <span className="text-highlighted">
                      {getPlanDisplayName(subscription.planName)}
                    </span>
                  </p>
                  <p className="text-secondary text-sm">
                    <span className="text-black font-semibold">
                      {formatPrice(subscription.amount, subscription.currency)}
                    </span>{' '}
                    {intervalLabel}
                  </p>
                </>
              )}
            </div>
          </div>

          {subscription && amountDue != null && (
            <div className="flex justify-between">
              <p className="text-black text-xl font-medium">{t('unpaid.totalTTC')}</p>
              <p className="text-black text-xl font-medium">
                {formatPrice(amountDue, subscription.currency)}
              </p>
            </div>
          )}

          <Separator />

          {canPay ? (
            <div className="space-y-6">
              <Title level={2}>{t('common.paymentByCard')}</Title>

              {paymentMethods && paymentMethods.length > 0 && !useNewCard && (
                <>
                  <UpgradePaymentMethodSelector
                    paymentMethods={paymentMethods}
                    selectedId={selectedPaymentMethodId}
                    onSelect={setSelectedPaymentMethodId}
                    disabled={isPaying}
                  />
                  <Button
                    onClick={handlePayWithSavedCard}
                    disabled={isPaying || !selectedPaymentMethodId}
                    className="w-full"
                  >
                    {isPaying && <Loader />}
                    {isPaying
                      ? t('unpaid.paying')
                      : subscription && amountDue != null
                        ? t('unpaid.payAmount', {
                            amount: formatPrice(amountDue, subscription.currency),
                          })
                        : t('unpaid.pay')}
                  </Button>
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-gray-500">{t('common.or')}</span>
                    </div>
                  </div>
                </>
              )}

              <div className="space-y-4">
                {useNewCard && paymentMethods && paymentMethods.length > 0 && (
                  <button
                    type="button"
                    className="text-sm text-highlighted cursor-pointer"
                    onClick={() => setUseNewCard(false)}
                  >
                    {t('common.useSavedCard')}
                  </button>
                )}

                {useNewCard || !paymentMethods?.length ? (
                  paymentIntentClientSecret && workspaceId && subscription && amountDue != null ? (
                    <PastDuePaymentForm
                      paymentIntentClientSecret={paymentIntentClientSecret}
                      amount={amountDue}
                      currency={subscription.currency}
                      workspaceId={workspaceId}
                      disabled={isPaying}
                      onSuccess={() => {}}
                    />
                  ) : null
                ) : (
                  <Button variant="outline" className="w-full" onClick={() => setUseNewCard(true)}>
                    {t('common.payWithNewCard')}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <p className="text-secondary text-sm">
              {t('unpaid.paymentNotAvailable')}
              {subscription?.latestInvoiceHostedUrl && (
                <>
                  {' '}
                  <a
                    href={subscription.latestInvoiceHostedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-highlighted underline"
                  >
                    {t('unpaid.payViaStripe')}
                  </a>
                </>
              )}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
